import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  useToast,
  Container,
  Heading,
  Alert,
  AlertIcon,
  AlertDescription,
  Code,
  Divider
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { sendEmailVerificationToUser, verifyEmail } from '../services/authService';

const EmailVerificationTest: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [isSending, setIsSending] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);

  const addDebugInfo = (info: string) => {
    setDebugInfo(prev => [...prev, `${new Date().toLocaleTimeString()}: ${info}`]);
  };

  const handleSendVerification = async () => {
    if (!user) {
      toast({ title: 'Not logged in', status: 'error' });
      return;
    }

    try {
      setIsSending(true);
      addDebugInfo(`Attempting to send verification to: ${user.email}`);
      addDebugInfo(`User email verified: ${user.emailVerified}`);
      
      await sendEmailVerificationToUser();
      
      addDebugInfo('Email verification sent successfully');
      toast({
        title: 'Verification email sent!',
        description: 'Check your email (including spam folder)',
        status: 'success',
        duration: 5000
      });
    } catch (error: any) {
      addDebugInfo(`Error: ${error.message}`);
      toast({
        title: 'Failed to send verification',
        description: error.message,
        status: 'error',
        duration: 5000
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleCheckVerification = async () => {
    if (!user) {
      toast({ title: 'Not logged in', status: 'error' });
      return;
    }

    try {
      setIsChecking(true);
      addDebugInfo('Checking email verification status...');
      
      const verified = await verifyEmail();
      
      addDebugInfo(`Email verification status: ${verified}`);
      
      if (verified) {
        toast({
          title: 'Email is verified!',
          status: 'success'
        });
      } else {
        toast({
          title: 'Email not verified yet',
          description: 'Please check your email and click the verification link',
          status: 'info'
        });
      }
    } catch (error: any) {
      addDebugInfo(`Error checking verification: ${error.message}`);
      toast({
        title: 'Failed to check verification',
        description: error.message,
        status: 'error'
      });
    } finally {
      setIsChecking(false);
    }
  };

  const clearDebugInfo = () => {
    setDebugInfo([]);
  };

  if (!user) {
    return (
      <Container maxW="container.md" py={8}>
        <Alert status="warning">
          <AlertIcon />
          <AlertDescription>Please log in to test email verification.</AlertDescription>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.md" py={8}>
      <VStack gap={6} align="stretch">
        <Heading size="lg" color="white">Email Verification Test</Heading>
        
        <Alert status="info">
          <AlertIcon />
          <AlertDescription>
            This page helps debug email verification issues. Check the console and debug info below.
          </AlertDescription>
        </Alert>

        <Box bg="gray.800" p={4} borderRadius="md">
          <VStack gap={3} align="stretch">
            <Text fontWeight="bold" color="white">User Information:</Text>
            <Text color="gray.300">Email: {user.email}</Text>
            <Text color="gray.300">Verified: {user.emailVerified ? 'Yes' : 'No'}</Text>
            <Text color="gray.300">UID: {user.uid}</Text>
          </VStack>
        </Box>

        <HStack gap={3}>
          <Button
            colorScheme="blue"
            onClick={handleSendVerification}
            isLoading={isSending}
            loadingText="Sending..."
            isDisabled={user.emailVerified}
          >
            Send Verification Email
          </Button>
          
          <Button
            colorScheme="green"
            onClick={handleCheckVerification}
            isLoading={isChecking}
            loadingText="Checking..."
          >
            Check Verification Status
          </Button>
        </HStack>

        {user.emailVerified && (
          <Alert status="success">
            <AlertIcon />
            <AlertDescription>
              Your email is already verified! This is normal for Google sign-in accounts.
            </AlertDescription>
          </Alert>
        )}

        {user.providerData && user.providerData.some(provider => provider.providerId === 'google.com') && (
          <Alert status="info">
            <AlertIcon />
            <AlertDescription>
              <VStack align="start" gap={2}>
                <Text fontWeight="bold">Google Sign-in Account Detected</Text>
                <Text fontSize="sm">Google accounts are automatically email verified. To test email verification, create a new account using email/password instead of Google sign-in.</Text>
              </VStack>
            </AlertDescription>
          </Alert>
        )}

        <Divider />

        <VStack gap={3} align="stretch">
          <HStack justify="space-between">
            <Text fontWeight="bold" color="white">Debug Information:</Text>
            <Button size="sm" onClick={clearDebugInfo}>Clear</Button>
          </HStack>
          
          <Box bg="gray.900" p={3} borderRadius="md" maxH="300px" overflowY="auto">
            {debugInfo.length === 0 ? (
              <Text color="gray.500">No debug information yet. Try sending a verification email.</Text>
            ) : (
              <VStack gap={1} align="stretch">
                {debugInfo.map((info, index) => (
                  <Code key={index} colorScheme="gray" fontSize="xs" p={1}>
                    {info}
                  </Code>
                ))}
              </VStack>
            )}
          </Box>
        </VStack>

        <Alert status="warning">
          <AlertIcon />
          <AlertDescription>
            <VStack align="start" gap={2}>
              <Text fontWeight="bold">Troubleshooting Tips:</Text>
              <Text fontSize="sm">1. Check your spam/junk folder</Text>
              <Text fontSize="sm">2. Make sure your email provider isn't blocking Firebase emails</Text>
              <Text fontSize="sm">3. Try a different email address (Gmail, Yahoo, etc.)</Text>
              <Text fontSize="sm">4. Check Firebase Console → Authentication → Users for email status</Text>
              <Text fontSize="sm">5. Verify your Firebase project has email verification enabled</Text>
            </VStack>
          </AlertDescription>
        </Alert>
      </VStack>
    </Container>
  );
};

export default EmailVerificationTest;
